import { useQuery } from '@tanstack/react-query';
import {
  AudienceInsightsDimensions,
  GoogleAudience
} from '@blisspointmedia/bpm-types/dist/YoutubePlanning';
import { awaitJSON, YoutubePlanningLambdaFetch } from '@src/utils/fetch-utils';
import { INTEREST_SEARCH_DIMENSIONS } from '../youtube-planning-constants';
import {
  filterDimensionListFromCombined,
  findSearchedInterest,
  mapToAudienceInsightMenuItem
} from '../youtube-planning-utils';
import {
  AudienceInsightMenuItem,
  GoogleAdsAccount,
  PrefillInterest
} from '../youtube-planning-types';

// Google audience-insight lookup. Throws on failure, including getAudienceInsights' error for a
// query with no matches (NO_AUDIENCE_INSIGHT_MATCHES).
export const searchAudienceInsights = async (
  cid: string,
  googleAdsAccount: GoogleAdsAccount,
  query: string,
  dimensions: AudienceInsightsDimensions[]
): Promise<AudienceInsightMenuItem[]> => {
  const res = await YoutubePlanningLambdaFetch('/getAudienceInsights', {
    method: 'GET',
    params: {
      company: cid,
      googleAdsAccount: googleAdsAccount.account_id,
      mccId: googleAdsAccount.mcc_id,
      queryString: query,
      dimensions: JSON.stringify(dimensions)
    }
  });
  const audienceInsights = await awaitJSON<GoogleAudience[]>(res);
  return mapToAudienceInsightMenuItem(audienceInsights);
};

// getAudienceInsights reports "no matches" as an error rather than an empty list.
export const NO_AUDIENCE_INSIGHT_MATCHES = 'No interest category matches found.';

// Best-effort variant, shared by the Build Recommendation typeahead and the master segments query
// below. Returns [] (rather than throwing) when there is no account yet or the call fails.
export const fetchAudienceInsights = async (
  cid: string,
  googleAdsAccount: GoogleAdsAccount | undefined,
  query: string,
  dimensions: AudienceInsightsDimensions[]
): Promise<AudienceInsightMenuItem[]> => {
  if (!googleAdsAccount) return [];
  try {
    return await searchAudienceInsights(cid, googleAdsAccount, query, dimensions);
  } catch {
    return [];
  }
};

export interface AudienceSegmentsMasterList {
  affinity: AudienceInsightMenuItem[];
  inMarket: AudienceInsightMenuItem[];
}

// Affinity & in-market user-interest results are independent of the search input; "cars" is a
// placeholder query used only to populate the master segments lists. Enabled once the account is
// available, then cached so switching tabs does not re-fetch it.
export const useAudienceSegmentsMasterList = (
  cid: string,
  googleAdsAccount: GoogleAdsAccount | undefined
): ReturnType<typeof useQuery<AudienceSegmentsMasterList>> =>
  useQuery({
    queryKey: ['youtubeAudienceSegments', cid, googleAdsAccount?.account_id],
    queryFn: async (): Promise<AudienceSegmentsMasterList> => {
      const segments = await fetchAudienceInsights(cid, googleAdsAccount, 'cars', [
        AudienceInsightsDimensions.AFFINITY_USER_INTEREST,
        AudienceInsightsDimensions.IN_MARKET_USER_INTEREST
      ]);
      return {
        affinity: filterDimensionListFromCombined(
          segments,
          AudienceInsightsDimensions.AFFINITY_USER_INTEREST
        ),
        inMarket: filterDimensionListFromCombined(
          segments,
          AudienceInsightsDimensions.IN_MARKET_USER_INTEREST
        )
      };
    },
    enabled: Boolean(googleAdsAccount)
  });

// A Creator Set's interest is selected only as a search result (see findSearchedInterest).
// A real no-match resolves to null; other errors throw, so the query retries them.
export const usePrefillInterestMatch = (
  cid: string,
  googleAdsAccount: GoogleAdsAccount | undefined,
  interest: PrefillInterest | undefined
): ReturnType<typeof useQuery<AudienceInsightMenuItem | null>> =>
  useQuery({
    queryKey: ['youtubePlanningPrefillInterest', cid, googleAdsAccount?.account_id, interest],
    queryFn: async (): Promise<AudienceInsightMenuItem | null> => {
      if (!interest || !googleAdsAccount) return null;
      try {
        const items = await searchAudienceInsights(
          cid,
          googleAdsAccount,
          interest.label,
          INTEREST_SEARCH_DIMENSIONS
        );
        return findSearchedInterest(items, interest) ?? null;
      } catch (e) {
        if (e.message === NO_AUDIENCE_INSIGHT_MATCHES) return null;
        throw e;
      }
    },
    enabled: Boolean(interest && googleAdsAccount)
  });
